import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faList,
  faChartLine,
  faSearch,
  faClock,
  faUser,
  faBuilding
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function MyOutreachHome() {
  const [activeTab, setActiveTab] = useState("entry");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const [outreaches, setOutreaches] = useState([
    {
      id: 1,
      title: "Investor Follow-up",
      target: "ABC Ventures",
      channel: "Email",
      reason: "Investor",
      effort: 15,
      prepHrs: 1,
      prepMins: 30,
      prepRemarks: "Reviewed pitch deck & past emails",
      response: "No Response"
    },
    {
      id: 2,
      title: "Supplier Call",
      target: "XYZ Industries",
      channel: "Call",
      reason: "Partnership",
      effort: 20,
      prepHrs: 0,
      prepMins: 20,
      prepRemarks: "Prepared negotiation points",
      response: "Positive"
    }
  ]);

  const totalEffort = (o) =>
    o.effort + o.prepHrs * 60 + o.prepMins;

  const filtered = outreaches.filter(
    (o) =>
      o.title.toLowerCase().includes(search.toLowerCase()) ||
      o.prepRemarks.toLowerCase().includes(search.toLowerCase())
  );

  /* ------------------ UI ------------------ */

  return (
    <div className="min-h-screen bg-black text-teal-300 p-3">
      {/* Header */}
      <h1 className="text-lg font-semibold mb-3">
        My Outreach
      </h1>

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Tab icon={faPlus} label="New" active={activeTab === "entry"} onClick={() => setActiveTab("entry")} />
        <Tab icon={faList} label="List" active={activeTab === "list"} onClick={() => setActiveTab("list")} />
        <Tab icon={faChartLine} label="Analysis" active={activeTab === "analysis"} onClick={() => setActiveTab("analysis")} />
      </div>

      {/* CONTENT */}
      {activeTab === "entry" && <NewEntry setOutreaches={setOutreaches} />}
      {activeTab === "list" && (
        <ListView
          search={search}
          setSearch={setSearch}
          data={filtered}
          setSelected={setSelected}
        />
      )}
      {activeTab === "analysis" && <Analysis data={outreaches} />}
      {selected && <DetailModal outreach={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/* ------------------ COMPONENTS ------------------ */

const Tab = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded flex flex-col items-center text-xs ${
      active ? "bg-teal-700 text-black" : "bg-gray-900"
    }`}
  >
    <FontAwesomeIcon icon={icon} />
    {label}
  </button>
);

/* -------- New Entry -------- */

function NewEntry({ setOutreaches }) {
  const [form, setForm] = useState({
    title: "",
    target: "",
    channel: "Call",
    reason: "Sales",
    effort: 0,
    prepHrs: 0,
    prepMins: 0,
    prepRemarks: ""
  });

  const save = () => {
    setOutreaches((prev) => [
      ...prev,
      { ...form, id: Date.now(), response: "No Response" }
    ]);
    alert("Outreach logged (dummy)");
  };

  return (
    <div className="space-y-3">
      <Input label="Outreach Title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <Input label="Target (Person / Company)" onChange={(e) => setForm({ ...form, target: e.target.value })} />

      <select className="w-full bg-gray-900 p-2 rounded">
        <option>Call</option>
        <option>Email</option>
        <option>WhatsApp</option>
        <option>LinkedIn</option>
      </select>

      <select className="w-full bg-gray-900 p-2 rounded">
        <option>Sales</option>
        <option>Partnership</option>
        <option>Investor</option>
        <option>Follow-up</option>
      </select>

      <Input type="number" label="Outreach Effort (minutes)" onChange={(e) => setForm({ ...form, effort: +e.target.value })} />

      <div className="grid grid-cols-2 gap-2">
        <Input type="number" label="Prep Hours" onChange={(e) => setForm({ ...form, prepHrs: +e.target.value })} />
        <Input type="number" label="Prep Minutes" onChange={(e) => setForm({ ...form, prepMins: +e.target.value })} />
      </div>

      <textarea
        placeholder="Preparedness remarks"
        className="w-full bg-gray-900 p-2 rounded"
        onChange={(e) => setForm({ ...form, prepRemarks: e.target.value })}
      />

      <button className="w-full bg-teal-700 text-black p-2 rounded" onClick={save}>
        Save Outreach
      </button>
    </div>
  );
}

/* -------- List -------- */

function ListView({ search, setSearch, data, setSelected }) {
  return (
    <>
      <div className="flex items-center bg-gray-900 p-2 rounded mb-3">
        <FontAwesomeIcon icon={faSearch} className="mr-2" />
        <input
          className="bg-transparent w-full outline-none"
          placeholder="Search by title or keywords"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        {data.map((o) => (
          <div
            key={o.id}
            onClick={() => setSelected(o)}
            className="bg-gray-900 p-2 rounded cursor-pointer"
          >
            <div className="flex justify-between">
              <span>{o.title}</span>
              <span className="text-xs">{o.response}</span>
            </div>
            <div className="text-xs opacity-70">
              {o.channel} • {o.reason}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------- Analysis -------- */

function Analysis({ data }) {
  const chartData = {
    labels: data.map((o) => o.title),
    datasets: [
      {
        label: "Total Effort (mins)",
        data: data.map((o) => o.effort + o.prepHrs * 60 + o.prepMins),
        backgroundColor: "#14b8a6"
      }
    ]
  };

  return (
    <div className="bg-gray-900 p-2 rounded">
      <Bar data={chartData} />
    </div>
  );
}

/* -------- Modal -------- */

function DetailModal({ outreach, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center p-4">
      <div className="bg-gray-900 p-4 rounded w-full max-w-md">
        <h2 className="font-semibold mb-2">{outreach.title}</h2>
        <p><FontAwesomeIcon icon={faUser} /> {outreach.target}</p>
        <p><FontAwesomeIcon icon={faClock} /> Total Effort: {outreach.effort + outreach.prepHrs * 60 + outreach.prepMins} mins</p>
        <p className="text-xs mt-2">{outreach.prepRemarks}</p>

        <button className="mt-3 w-full bg-teal-700 text-black p-2 rounded" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/* -------- Input -------- */

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-xs opacity-70">{label}</label>
    <input {...props} className="w-full bg-gray-900 p-2 rounded" />
  </div>
);
